import React, { useState, useEffect, useRef, useMemo, useLayoutEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import backend from "../../backend";

/**
 * EntityLookup — búsqueda de clientes/proveedores con dropdown Portal.
 *
 * Props:
 *   value       { entityId, name, rut, email, phone, type } | null
 *   onChange    (entity | null) => void
 *   type        "client" | "provider" | "both" | "any"  (default "any")
 *   placeholder string
 *   disabled    boolean
 *   allowCreate boolean — si no hay resultados, ofrece crear la entidad con el
 *               texto escrito (solo con type "provider" o "client"). POST /entity.
 */
// Nombre comparable para detectar duplicados: minúsculas, sin tildes, sin puntos
// ni comas, sin sufijo societario (S.A., SpA, Ltda., Limitada, EIRL) y sin espacios.
const SUFIJO_SOCIETARIO = /\s+(sa|spa|ltda|limitada|eirl)$/;
const normNombre = s => {
  let n = String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "")
    .replace(/[.,]/g, "").replace(/\s+/g, " ").trim();
  while (SUFIJO_SOCIETARIO.test(n)) n = n.replace(SUFIJO_SOCIETARIO, "");
  return n.replace(/\s+/g, "");
};

const EntityLookup = ({
  value,
  onChange,
  type = "any",
  placeholder = "Buscar por nombre o RUT...",
  disabled = false,
  allowCreate = false,
}) => {
  const [query,       setQuery]       = useState(value?.name || "");
  const [entities,    setEntities]    = useState([]);
  const [open,        setOpen]        = useState(false);
  const [dropdownRect,setDropdownRect]= useState(null);
  const [creating,    setCreating]    = useState(false);
  // allowCreate: solo se ofrece crear si la lista cargó bien (si el GET falló no
  // sabemos si la entidad ya existe), y se confirma en un paso con RUT opcional.
  const [loaded,      setLoaded]      = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [rutNuevo,    setRutNuevo]    = useState("");
  const [aviso,       setAviso]       = useState("");

  const inputRef    = useRef(null);
  const dropdownRef = useRef(null);
  const containerRef= useRef(null);

  // Pre-load full entity list once
  useEffect(() => {
    backend.get("entity")
      .then(r => { setEntities(r.data || []); setLoaded(true); })
      .catch(() => setLoaded(false));
  }, []);

  // Sync display when value changes externally
  useEffect(() => {
    setQuery(value?.name || "");
  }, [value]);

  // Compute dropdown rect after open changes
  useLayoutEffect(() => {
    if (open && inputRef.current) {
      const r = inputRef.current.getBoundingClientRect();
      setDropdownRect({ top: r.bottom + 2, left: r.left, width: r.width });
    }
  }, [open]);

  // Reposition on scroll/resize
  useEffect(() => {
    if (!open) return;
    const reposition = () => {
      if (inputRef.current) {
        const r = inputRef.current.getBoundingClientRect();
        setDropdownRect({ top: r.bottom + 2, left: r.left, width: r.width });
      }
    };
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open]);

  // Close on outside click
  useEffect(() => {
    const handler = e => {
      if (!containerRef.current?.contains(e.target) && !dropdownRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = useMemo(() => {
    let list = entities;
    if (type !== "any") {
      list = list.filter(e => e.type === type || e.type === "both");
    }
    if (!query.trim()) return list.slice(0, 10);
    const q = query.toLowerCase();
    return list.filter(e =>
      (e.name  || "").toLowerCase().includes(q) ||
      (e.rut   || "").toLowerCase().includes(q) ||
      (e.email || "").toLowerCase().includes(q)
    ).slice(0, 10);
  }, [entities, query, type]);

  const handleSelect = useCallback(entity => {
    onChange?.({
      entityId: entity._id,
      name: entity.name,
      rut: entity.rut,
      email: entity.email,
      phone: entity.phone,
      type: entity.type,
    });
    setQuery(entity.name || "");
    setOpen(false);
  }, [onChange]);

  // 13-oct — Alta rápida desde el buscador: en el celular no hay cómo ir a la
  // ficha de entidades a mitad de una solicitud de compra. Se crea solo con el
  // nombre; RUT y datos de contacto se completan después en la ficha.
  const puedeCrear = allowCreate && loaded && (type === "provider" || type === "client");

  // Paso 1: antes de ofrecer crear, se busca en TODAS las entidades (clientes
  // incluidos) por nombre normalizado, para no duplicar "Aceros X S.A." como
  // "aceros x". Si existe, se selecciona. No hay PATCH /entity/:id (el PATCH
  // existente va por body._id), así que no se cambia el tipo desde acá: se avisa.
  const handlePedirCrear = useCallback(() => {
    const name = query.trim();
    if (!name || creating) return;
    const clave = normNombre(name);
    const existente = entities.find(e => normNombre(e.name) === clave);
    if (existente) {
      handleSelect(existente);
      if (existente.type !== type && existente.type !== "both") {
        setAviso(type === "provider"
          ? "Ya existe como cliente, pide que la marquen como proveedor"
          : "Ya existe como proveedor, pide que la marquen como cliente");
      }
      return;
    }
    setRutNuevo("");
    setConfirmando(true);
  }, [query, creating, entities, type, handleSelect]);

  // Paso 2: confirmado, se crea con el RUT si se escribió.
  const handleCreate = useCallback(async () => {
    const name = query.trim();
    if (!name || creating) return;
    const rut = rutNuevo.trim();
    setCreating(true);
    try {
      const { data } = await backend.post("entity", rut ? { name, type, rut } : { name, type });
      setEntities(prev => [...prev, data]);
      setConfirmando(false);
      handleSelect(data);
      if (!rut) setAviso(`Completa el RUT en la ficha del ${type === "provider" ? "proveedor" : "cliente"}`);
    } catch (err) {
      console.error("[EntityLookup] crear:", err);
      alert("No se pudo crear: " + (err?.response?.data || err.message));
    } finally {
      setCreating(false);
    }
  }, [query, rutNuevo, type, creating, handleSelect]);

  const handleClear = useCallback(() => {
    onChange?.(null);
    setQuery("");
  }, [onChange]);

  const typeLabel = t => t === "provider" ? "Prov" : t === "both" ? "C/P" : "Cli";
  const typeColors = t => t === "provider"
    ? { bg: "#1a3a25", color: "#2ecc71" }
    : t === "both"
    ? { bg: "#1a2535", color: "#9b59b6" }
    : { bg: "#0d1e2e", color: "#3498db" };

  const dropdown = open && dropdownRect
    ? ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: "fixed",
            top: dropdownRect.top,
            left: dropdownRect.left,
            width: Math.max(dropdownRect.width, 280),
            background: "#1a1a1a",
            border: "1px solid #2e2e2e",
            borderRadius: 6,
            maxHeight: 280,
            overflowY: "auto",
            zIndex: 10000,
            boxShadow: "0 4px 16px rgba(0,0,0,0.6)",
          }}
        >
          {filtered.length === 0 && (
            <div style={{ padding: "10px", color: "#aaa", fontSize: 11, textAlign: "center" }}>
              Sin resultados{type !== "any" ? ` (${type === "provider" ? "proveedores" : "clientes"})` : ""}
            </div>
          )}
          {filtered.length === 0 && puedeCrear && query.trim() && !confirmando && (
            <div
              role="button"
              onMouseDown={e => { e.preventDefault(); handlePedirCrear(); }}
              style={{
                padding: "12px 10px", cursor: "pointer", fontSize: 13, fontWeight: 600,
                color: "var(--tab-verde)", borderTop: "1px solid var(--tab-borde)",
              }}
            >
              {`+ Crear ${type === "provider" ? "proveedor" : "cliente"} «${query.trim()}»`}
            </div>
          )}
          {filtered.length === 0 && puedeCrear && query.trim() && confirmando && (
            <div style={{ padding: "10px", borderTop: "1px solid var(--tab-borde)", fontSize: 12 }}>
              <div style={{ color: "var(--tab-texto)", marginBottom: 8 }}>
                ¿Crear {type === "provider" ? "proveedor" : "cliente"} «{query.trim()}»?
              </div>
              <input
                className="bini-input"
                value={rutNuevo}
                onChange={e => setRutNuevo(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleCreate()}
                placeholder="RUT (opcional)"
                autoComplete="off"
                style={{ fontSize: 12, marginBottom: 8 }}
              />
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  disabled={creating}
                  onClick={handleCreate}
                  style={{
                    flex: 1, padding: "8px 12px", fontSize: 12, fontWeight: 600, borderRadius: 6,
                    border: "1px solid var(--tab-borde)", background: "var(--tab-boton)",
                    color: "var(--tab-verde)", cursor: creating ? "wait" : "pointer",
                  }}
                >{creating ? "Creando…" : "Crear"}</button>
                <button
                  type="button"
                  disabled={creating}
                  onClick={() => setConfirmando(false)}
                  style={{
                    padding: "8px 12px", fontSize: 12, borderRadius: 6, border: "1px solid var(--tab-borde)",
                    background: "transparent", color: "var(--tab-suave)", cursor: "pointer",
                  }}
                >Cancelar</button>
              </div>
            </div>
          )}
          {filtered.map(entity => {
            const tc = typeColors(entity.type);
            return (
              <div
                key={entity._id}
                onMouseDown={e => { e.preventDefault(); handleSelect(entity); }}
                style={{
                  padding: "8px 10px", cursor: "pointer",
                  borderBottom: "1px solid #1f1f1f",
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "#242424"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >
                <div>
                  <div style={{ fontSize: 12, color: "#fff", fontWeight: 500 }}>{entity.name}</div>
                  <div style={{ fontSize: 10, color: "#cbd5e1", marginTop: 2 }}>
                    {entity.rut || "sin RUT"}
                    {entity.email ? ` · ${entity.email}` : ""}
                  </div>
                </div>
                <span style={{
                  fontSize: 9, padding: "2px 6px", borderRadius: 3,
                  background: tc.bg, color: tc.color, textTransform: "uppercase", flexShrink: 0,
                }}>
                  {typeLabel(entity.type)}
                </span>
              </div>
            );
          })}
        </div>,
        document.body
      )
    : null;

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <input
        ref={inputRef}
        className="bini-input"
        value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true); setConfirmando(false); setAviso(""); onChange?.(null); }}
        onFocus={() => setOpen(true)}
        onKeyDown={e => e.key === "Escape" && setOpen(false)}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        style={{ paddingRight: value?.entityId ? 28 : undefined }}
      />
      {value?.entityId && !disabled && (
        <button
          type="button"
          onClick={handleClear}
          style={{
            position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)",
            background: "none", border: "none", color: "#aaa", cursor: "pointer", fontSize: 14, lineHeight: 1,
          }}
        >×</button>
      )}
      {aviso && (
        <div style={{ fontSize: 11, color: "var(--tab-ambar)", marginTop: 4 }}>{aviso}</div>
      )}
      {dropdown}
    </div>
  );
};

export default EntityLookup;
